/**
 * Comprehensive error messages dictionary for Ukrainian localization
 * Covers Firebase Auth, Firestore, Storage, and custom application errors
 */

export const errorMessages: Record<string, string> = {
  // ==================== FIREBASE AUTH ERRORS ====================
  // Invalid credentials and input
  'auth/invalid-email': 'Невірний формат email',
  'auth/invalid-password': 'Невірний пароль',
  'auth/invalid-credential': 'Невірні облікові дані',
  'auth/invalid-verification-code': 'Невірний код підтвердження',
  'auth/invalid-verification-id': 'Невірний ID підтвердження',
  'auth/invalid-phone-number': 'Невірний номер телефону',
  'auth/invalid-user-token': 'Недійсний токен користувача',
  'auth/invalid-action-code': 'Посилання недійсне або застаріло',
  'auth/invalid-api-key': 'Недійсний API ключ',
  'auth/invalid-app-credential': 'Недійсні облікові дані додатку',
  'auth/invalid-app-id': 'Недійсний ID додатку',
  'auth/invalid-auth-event': 'Недійсна подія автентифікації',
  'auth/invalid-continue-uri': 'Недійсна URI для продовження',
  'auth/invalid-cordova-configuration': 'Недійсна конфігурація Cordova',
  'auth/invalid-custom-token': 'Недійсний кастомний токен',
  'auth/invalid-default-rollover-interval': 'Недійсний інтервал ролловера',
  'auth/invalid-dynamic-link-domain': 'Недійсний домен динамічного посилання',
  'auth/invalid-emulator-scheme': 'Недійсна схема емулятора',
  'auth/invalid-message-payload': 'Недійсний payload повідомлення',
  'auth/invalid-multi-factor-session': 'Недійсна сесія багатофакторної автентифікації',
  'auth/invalid-oauth-client-id': 'Недійсний OAuth client ID',
  'auth/invalid-oauth-provider': 'Недійсний OAuth провайдер',
  'auth/invalid-persistence-type': 'Недійсний тип персистентності',
  'auth/invalid-provider-data': 'Недійсні дані провайдера',
  'auth/invalid-provider-id': 'Недійсний ID провайдера',
  'auth/invalid-recipient-email': 'Недійсний email отримувача',
  'auth/invalid-sender': 'Недійсний відправник',
  'auth/invalid-session-info': 'Недійсна інформація про сесію',
  'auth/invalid-tenant-id': 'Недійсний ID орендаря',

  // User-related errors
  'auth/user-not-found': 'Користувача не знайдено',
  'auth/user-disabled': 'Обліковий запис заблоковано',
  'auth/user-token-expired': 'Токен користувача закінчився',
  'auth/user-mismatch': 'Користувач не відповідає очікуваному',
  'auth/user-signed-out': 'Користувач вийшов з системи',
  'auth/email-already-in-use': 'Цей email вже використовується',
  'auth/email-not-verified': 'Email не підтверджено',
  'auth/weak-password': 'Пароль має містити мінімум 8 символів, включаючи великі та малі літери, цифри та спеціальні символи',
  'auth/wrong-password': 'Невірний пароль',
  'auth/account-exists-with-different-credential': 'Акаунт вже існує з іншим методом входу',
  'auth/credential-already-in-use': 'Облікові дані вже використовуються',

  // Session and authentication errors
  'auth/requires-recent-login': 'Потрібна повторна авторизація для безпеки',
  'auth/unauthenticated': 'Користувач не авторизований',
  'auth/unauthorized-domain': 'Несанкціонований домен',

  // Popup and redirect errors
  'auth/popup-closed-by-user': 'Вікно авторизації закрито',
  'auth/popup-blocked': 'Спливаюче вікно заблоковано браузером',
  'auth/redirect-cancelled-by-user': 'Редірект скасовано користувачем',
  'auth/redirect-operation-pending': 'Операція редіректу вже триває',

  // Network and timeout errors
  'auth/network-request-failed': 'Помилка мережі. Перевірте підключення до інтернету',
  'auth/timeout': 'Час очікування запиту вичерпано',

  // Rate limiting
  'auth/too-many-requests': 'Забагато спроб. Спробуйте пізніше',
  'auth/quota-exceeded': 'Перевищено ліміт запитів',

  // Code expiration
  'auth/expired-action-code': 'Посилання закінчилося',
  'auth/expired-phone-number': 'Термін дії номера телефону закінчився',
  'auth/expired-popup-request': 'Термін запиту спливаючого вікна закінчився',
  'auth/expired-verification-code': 'Код підтвердження закінчився',
  'auth/expired-verification-id': 'ID підтвердження закінчився',

  // Multi-factor authentication
  'auth/missing-multi-factor-info': 'Відсутня інформація про багатофакторну автентифікацію',
  'auth/missing-multi-factor-session': 'Відсутня сесія багатофакторної автентифікації',
  'auth/multi-factor-info-not-found': 'Інформацію про MFA не знайдено',
  'auth/multi-factor-auth-required': 'Потрібна багатофакторна автентифікація',
  'auth/second-factor-already-in-use': 'Другий фактор вже використовується',
  'auth/maximum-second-factor-count-exceeded': 'Перевищено максимальну кількість других факторів',

  // Missing parameters
  'auth/missing-android-pkg-name': 'Відсутнє ім\'я Android пакету',
  'auth/missing-app-credential': 'Відсутні облікові дані додатку',
  'auth/missing-auth-domain': 'Відсутній домен автентифікації',
  'auth/missing-code': 'Відсутній код',
  'auth/missing-continue-uri': 'Відсутня URI для продовження',
  'auth/missing-iframe-start': 'Відсутній початок iframe',
  'auth/missing-ios-bundle-id': 'Відсутній iOS bundle ID',
  'auth/missing-or-invalid-nonce': 'Відсутній або недійсний nonce',
  'auth/missing-password': 'Введіть пароль',
  'auth/missing-email': 'Введіть email',
  'auth/missing-verification-code': 'Введіть код підтвердження',
  'auth/missing-verification-id': 'Відсутній ID підтвердження',
  'auth/missing-phone-number': 'Введіть номер телефону',
  'auth/missing-session-info': 'Відсутня інформація про сесію',
  'auth/missing-tenant-id': 'Відсутній ID орендаря',

  // Configuration errors
  'auth/app-not-authorized': 'Додаток не авторизований',
  'auth/app-not-installed': 'Додаток не встановлено',
  'auth/keychain-error': 'Помилка зв\'язки ключів',
  'auth/internal-error': 'Внутрішня помилка сервера',
  'auth/unsupported-persistence-type': 'Непідтримуваний тип персистентності',
  'auth/unsupported-tenant-operation': 'Непідтримувана операція для орендаря',
  'auth/tenant-id-mismatch': 'Невідповідність ID орендаря',
  'auth/web-storage-unsupported': 'Веб-сховище не підтримується',
  'auth/operation-not-supported-in-this-environment': 'Операція не підтримується в цьому середовищі',
  'auth/blocked-origin': 'Заблоковане джерело',

  // ==================== FIRESTORE ERRORS ====================
  'permission-denied': 'Недостатньо прав для виконання операції',
  'not-found': 'Документ не знайдено',
  'already-exists': 'Документ вже існує',
  'resource-exhausted': 'Ресурс вичерпано',
  'failed-precondition': 'Помилка передумови',
  'aborted': 'Операцію скасовано',
  'out-of-range': 'Значення поза допустимим діапазоном',
  'unimplemented': 'Операція не реалізована',
  'internal': 'Внутрішня помилка',
  'unavailable': 'Сервіс тимчасово недоступний',
  'data-loss': 'Критична помилка: можлива втрата даних',
  'deadline-exceeded': 'Термін виконання операції вичерпано',
  'cancelled': 'Операцію скасовано користувачем',

  // ==================== STORAGE ERRORS ====================
  'storage/unknown': 'Невідома помилка сховища',
  'storage/object-not-found': 'Файл не знайдено',
  'storage/bucket-not-found': 'Сховище не знайдено',
  'storage/project-not-found': 'Проєкт не знайдено',
  'storage/quota-exceeded': 'Перевищено ліміт сховища',
  'storage/unauthenticated': 'Потрібна автентифікація для доступу',
  'storage/unauthorized': 'Недостатньо прав для доступу',
  'storage/retry-limit-exceeded': 'Перевищено ліміт повторних спроб',
  'storage/invalid-checksum': 'Помилка контрольної суми файлу',
  'storage/canceled': 'Завантаження скасовано',
  'storage/invalid-event-name': 'Недійсна назва події',
  'storage/invalid-url': 'Недійсне посилання на файл',
  'storage/invalid-argument': 'Недійсний аргумент',
  'storage/no-default-bucket': 'Не налаштоване сховище за замовчуванням',
  'storage/cannot-slice-blob': 'Неможливо обробити файл',
  'storage/server-file-wrong-size': 'Розмір файлу на сервері не відповідає',

  // ==================== CUSTOM APP ERRORS ====================
  // Authentication
  'custom/not-authenticated': 'Користувач не авторизований',
  'custom/permission-denied': 'Доступ заборонено',
  'custom/user-deleted': 'Акаунт видалено',
  'custom/session-expired': 'Сесія закінчилась',
  'custom/firebase-not-configured': 'Firebase не налаштовано',
  'custom/unknown-error': 'Невідома помилка',
  'custom/email-not-verified': 'Email не підтверджено',
  'custom/invalid-role': 'Недійсна роль користувача',
  'custom/role-required': 'Необхідно обрати роль',

  // Validation
  'custom/invalid-input': 'Невірні дані',
  'custom/missing-field': 'Заповніть всі обов\'язкові поля',
  'custom/invalid-email-format': 'Невірний формат email',
  'custom/weak-password': 'Пароль занадто слабкий',
  'custom/passwords-dont-match': 'Паролі не співпадають',
  'custom/invalid-name': 'Невірне ім\'я',

  // File upload
  'custom/file-too-large': 'Файл занадто великий',
  'custom/invalid-file-type': 'Непідтримуваний тип файлу',
  'custom/upload-failed': 'Помилка завантаження файлу',
  'custom/image-processing-error': 'Помилка обробки зображення',

  // Network
  'custom/network-error': 'Помилка мережі',
  'custom/server-error': 'Помилка сервера',
  'custom/timeout-error': 'Час очікування вичерпано',
  'custom/offline': 'Відсутнє підключення до інтернету',

  // Limits - custom limit errors from Cloud Functions
  'limit/projects': 'Досягнуто ліміт проектів',
  'limit/demo-projects': 'Досягнуто ліміт демо-проектів',
  'limit/versions': 'Досягнуто ліміт версій',
  'limit/screens': 'Досягнуто ліміт екранів',
  'limit/events': 'Досягнуто ліміт подій',
  'limit/members': 'Досягнуто ліміт учасників',

  // ==================== DEFAULT ====================
  'default': 'Сталася помилка. Спробуйте ще раз',
  'unknown': 'Сталася невідома помилка'
};

/**
 * Success messages in Ukrainian
 */
export const successMessages: Record<string, string> = {
  // Auth success
  'auth/signin-success': 'Вхід виконано успішно',
  'auth/signup-success': 'Реєстрація успішна! Перевірте email',
  'auth/signout-success': 'Вихід виконано успішно',
  'auth/password-reset-sent': 'Посилання для відновлення пароля надіслано',
  'auth/password-changed': 'Пароль успішно змінено',
  'auth/email-verified': 'Email успішно підтверджено',
  'auth/verification-email-sent': 'Лист з підтвердженням надіслано',
  'auth/profile-updated': 'Профіль оновлено',
  'auth/email-changed': 'Email успішно змінено',
  'auth/account-deleted': 'Акаунт видалено',
  'auth/role-selected': 'Роль успішно обрана',

  // General success
  'default/success': 'Операція виконана успішно',
  'default/saved': 'Збережено',
  'default/updated': 'Оновлено',
  'default/deleted': 'Видалено',
  'default/created': 'Створено',
  'default/copied': 'Скопійовано',
  'default/exported': 'Експортовано',
  'default/imported': 'Імпортовано'
};
